import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const Login = () => {
  const [email, setEmail] = useState(() => localStorage.getItem("aurea_remember_email") || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => !!localStorage.getItem("aurea_remember_email"));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (remember) localStorage.setItem("aurea_remember_email", email);
      else localStorage.removeItem("aurea_remember_email");
      login(data.token, data.user);
      navigate("/gateway");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="font-serif text-4xl text-brand block text-center mb-2">Aurea</Link>
        <p className="text-center text-ink-mute mb-8">Welcome back. Take a breath.</p>
        <form onSubmit={submit} className="bg-white rounded-3xl border border-linen-3 p-8 space-y-5" data-testid="login-form">
          {error && <div className="bg-warmsoft text-warmsoft-text text-sm rounded-2xl px-4 py-3" data-testid="login-error">{error}</div>}
          <div className="space-y-2">
            <Label className="text-ink-2">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-2xl bg-linen border-linen-3 h-12" data-testid="login-email" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-ink-2">Password</Label>
              <Link to="/forgot-password" className="text-xs text-accent hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-2xl bg-linen border-linen-3 h-12" data-testid="login-password" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-me" className="data-[state=checked]:bg-brand data-[state=checked]:border-brand" />
            <Label htmlFor="remember" className="text-sm text-ink-2 cursor-pointer">Remember me on this device</Label>
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-brand hover:bg-brand-hover text-linen h-12" data-testid="login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <p className="text-center text-sm text-ink-2 mt-6">
          New here? <Link to="/register" className="text-accent font-medium" data-testid="go-register">Create an account</Link>
        </p>
        <p className="text-center text-[11px] text-ink-mute mt-4">© {new Date().getFullYear()} Aurea · Damian Alan Moses</p>
      </div>
    </div>
  );
};

export default Login;
